import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { BookingsPageMode } from 'src/store/bookings/types';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface MyBookingsPageModeContextValue {
  mode: BookingsPageMode | undefined;
  setMode: (mode: BookingsPageMode) => void;
}

export const MyBookingsPageModeContext = createContext<
  MyBookingsPageModeContextValue | undefined
>(undefined);
MyBookingsPageModeContext.displayName = 'MyBookingsPageModeContext';

interface MyBookingsPageModeProviderProps {
  children?: ReactNode;
}

export const MyBookingsPageModeProvider: FC<
  MyBookingsPageModeProviderProps
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = (searchParams.get('mode') ?? undefined) as
    | BookingsPageMode
    | undefined;

  const setMode = useCallback(
    (value: BookingsPageMode) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('mode', value);
        return next;
      });
      dispatch(sidesheetActions.close());
    },
    [dispatch, setSearchParams],
  );

  useEffect(() => {
    if (!mode) {
      setMode(BookingsPageMode.Actual);
    }
  }, [mode, setMode]);

  const contextValue = useMemo((): MyBookingsPageModeContextValue => {
    return {
      mode,
      setMode,
    };
  }, [mode, setMode]);

  return (
    <MyBookingsPageModeContext.Provider value={contextValue}>
      {children}
    </MyBookingsPageModeContext.Provider>
  );
};

export const useMyBookingsPageMode = (): MyBookingsPageModeContextValue =>
  useRequiredContext(MyBookingsPageModeContext);
